// Каркас «Табеля» (FR-036; contracts/ui.md, «Каркас»): меню на холсте, белый лист, шапка листа.
// До 900 px меню выезжает поверх экрана и закрывается при переходе; до 640 px — нижняя
// навигация, лента уведомлений и окна — снизу на весь экран. Слои каркаса: палитра ⌘K (T050),
// список клавиш, полоса «Нет связи», лента уведомлений, клавиши (T051), окно «Войдите снова»
// (T045). Окна создания — из «Создать ▾», палитры и разделов. Версия сборки — в углу: внизу
// меню, а до 900 px, когда меню выезжает поверх экрана, — в углу листа (FR-042б). Режим фокуса
// (ux-dense: протокол «Во весь экран») шире 640 px скрывает шапку листа и меню приложения.
// Шире 900 px правую границу развёрнутого меню тянут мышью и клавишами (0.22.4, SidebarResizer).
//
// 030 (T019, T059; FR-031, FR-032; ASG-DESK-15, ASG-ONB-15): сеанс по ссылке — узкое меню, но
// шапка как у всех (конституция X): палитра ⌘K — только его разделы («Мои задачи», «Помощь»)
// и «Выйти», лента уведомлений; списка клавиш нет, `?` и N не действуют. Окно «Помощь» —
// у каркаса (HelpHost): его открывают меню, шапка, палитра, нижняя навигация и экраны (useHelp);
// к вопросу уходит название экрана — крошка маршрута, без неё — адрес.
//
// 033 (T012; FR-009): полоса «Все сообщения остановлены» — под полосой «Нет связи», над
// содержимым, у тех, кто может остановить и возобновить сообщения (StoppedBanner).
import { SideSheet, TableDensityProvider, cx, media, useIsPhone, useMediaQuery } from '@kp/ui';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { Outlet, useMatches, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { refetchStale } from '../app/query-client.ts';
import { useTableDensityPreference } from '../app/view-preferences.ts';
import { HelpDialog } from '../features/help/HelpDialog.tsx';
import { BottomNav } from './BottomNav.tsx';
import { BuildVersion } from './BuildVersion.tsx';
import { CommandPalette } from './CommandPalette.tsx';
import { ShellProvider, useShell } from './context.tsx';
import { CreateModals, useCreateItems } from './create.tsx';
import { goTargetOf, useShellKeys } from './keys.ts';
import { NotificationsSheet } from './NotificationsFeed.tsx';
import { OfflineBanner } from './OfflineBanner.tsx';
import { INBOX_PATH, can, sections } from './permissions.ts';
import { ReauthModal } from './ReauthModal.tsx';
import { rememberRecent } from './recent.ts';
import { meQueryKey, useBrowserTimeZone, useMe } from './session.ts';
import { sectionLinks } from './settings-sections.ts';
import { ShortcutsDialog } from './ShortcutsDialog.tsx';
import { SidebarContent } from './Sidebar.tsx';
import { SidebarResizer, useSidebarWidth } from './SidebarResizer.tsx';
import { StoppedBanner } from './StoppedBanner.tsx';
import { Topbar } from './Topbar.tsx';

/**
 * Каркас со страницей маршрута. children — своя страница вместо маршрута: «Страница не найдена»
 * для неизвестного адреса (T102), у которого нет маршрута внутри каркаса.
 */
export function AppShell({ children }: { children?: ReactNode }) {
  // Плотность таблиц — настройка вида на этом устройстве (раздел «Вид» настроек, 007).
  const density = useTableDensityPreference();
  return (
    <ShellProvider>
      <TableDensityProvider density={density}>
        <ShellLayout>{children}</ShellLayout>
      </TableDensityProvider>
    </ShellProvider>
  );
}

function ShellLayout({ children }: { children?: ReactNode }) {
  const shell = useShell();
  const { linkSession } = useMe();
  const overlayMenu = useMediaQuery(media.tablet);
  const isPhone = useIsPhone();
  const sidebar = useSidebarWidth();
  // Граница меню тянется, только пока меню на холсте развёрнуто и видно.
  const resizable = !overlayMenu && !shell.menuCollapsed && !shell.focusMode;
  useRefreshSessionOnNavigation();
  useRefetchOnReturn();
  // 031 (FR-028): пояс браузера для писем — один раз, если у учётной записи его нет.
  useBrowserTimeZone();
  useRememberSections();
  useKeys(overlayMenu);

  return (
    <div
      className={cx(
        'kp-app',
        !overlayMenu && shell.menuCollapsed && 'is-collapsed',
        shell.focusMode && !isPhone && 'is-focus',
      )}
      style={resizable ? ({ '--kp-sidebar-w': `${sidebar.width}px` } as CSSProperties) : undefined}
    >
      {overlayMenu ? (
        <SideSheet open={shell.menuOpen} onOpenChange={shell.setMenuOpen} title="Меню">
          <SidebarContent
            onNavigate={() => shell.setMenuOpen(false)}
            showBuild={false}
            keyHints={false}
          />
        </SideSheet>
      ) : (
        <aside className="kp-sidebar">
          <SidebarContent collapsed={shell.menuCollapsed} />
          {resizable ? <SidebarResizer sidebar={sidebar} /> : null}
        </aside>
      )}
      <div className="kp-sheet">
        <Topbar />
        <OfflineBanner />
        <StoppedBanner />
        {children ?? <Outlet />}
        {overlayMenu ? <BuildVersion className="kp-build-corner" /> : null}
      </div>
      {isPhone ? (
        <>
          <BottomNav />
          <NotificationsSheet />
        </>
      ) : null}
      <CommandPalette />
      {linkSession ? null : <ShortcutsDialog />}
      <CreateModals />
      <HelpHost />
      <ReauthModal />
    </div>
  );
}

/**
 * Окно «Помощь» каркаса (030, FR-032): экран — крошка маршрута («Мои задачи»), без неё — адрес;
 * пункт — от экрана, который открыл окно. Каждое открытие — с чистого поля.
 */
function HelpHost() {
  const { help, closeHelp } = useShell();
  const crumb = useMatches({
    select: (matches) => matches.map((match) => match.staticData.crumb).findLast(Boolean),
  });
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (!help) return null;
  return (
    <HelpDialog
      key={help.serial}
      open
      onOpenChange={(open) => {
        if (!open) closeHelp();
      }}
      screen={crumb ?? pathname}
      itemId={help.itemId}
      itemLabel={help.itemLabel}
      returnFocusRef={help.returnFocusRef}
    />
  );
}

/**
 * Права меняются на ходу (роль снята, срок роли закончился): при переходе в другой раздел
 * сеанс перечитывается, и меню обновляется (spec, «Граничные случаи»).
 */
function useRefreshSessionOnNavigation() {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const previous = useRef(pathname);
  useEffect(() => {
    if (previous.current === pathname) return;
    previous.current = pathname;
    void queryClient.invalidateQueries({ queryKey: meQueryKey });
  }, [pathname, queryClient]);
}

/**
 * Вкладка снова на экране — устаревшие данные экрана перечитываются фоновыми запросами: их никто
 * не просил, и сеанс они не продлевают (FR-008б, T118). Подписка — на focusManager TanStack: он
 * следит за visibilitychange и сам продолжает повторы, остановленные в скрытой вкладке.
 */
function useRefetchOnReturn() {
  const queryClient = useQueryClient();
  useEffect(
    () =>
      focusManager.subscribe((focused) => {
        if (focused) void refetchStale(queryClient);
      }),
    [queryClient],
  );
}

/** Открытый раздел попадает в «Недавние» палитры; записи добавляют свои экраны. */
function useRememberSections() {
  const me = useMe();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const userId = me.user.id;
  useEffect(() => {
    if (pathname === '/') return;
    const item = sectionLinks(me.permissions, me).find((nav) => nav.to === pathname);
    if (item) {
      rememberRecent(userId, { key: item.to, label: item.label, href: item.to, kind: 'section' });
    }
  }, [pathname, userId, me]);
}

/**
 * Клавиши каркаса: ⌘K, /, ?, [, N, G затем H, I, U, J, T, S. Сеанс по ссылке (030, T059) — ⌘K и `/`
 * (палитра его разделов: искать ему нечего, `/` открывает её же), `[`, G H и G I (032: «Входящие»
 * есть и в его меню); списка клавиш и «Создать» у него нет.
 */
function useKeys(overlayMenu: boolean) {
  const shell = useShell();
  const me = useMe();
  const navigate = useNavigate();
  const createItems = useCreateItems();
  const full = !me.linkSession;
  useShellKeys({
    togglePalette: () => shell.setPaletteOpen(!shell.paletteOpen),
    openSearch: () => shell.setPaletteOpen(true, 'search'),
    openShortcuts: () => {
      if (full) shell.setShortcutsOpen(true);
    },
    toggleMenu: () => (overlayMenu ? shell.setMenuOpen(!shell.menuOpen) : shell.toggleMenu()),
    openCreateMenu: () => {
      if (full && createItems.length > 0) shell.setCreateMenuOpen(true);
    },
    go: (key) => {
      const target = goTargetOf(key, can(me.permissions, sections.jobs.operations));
      if (target === 'home') void navigate({ to: '/' });
      else if (target === 'inbox') void navigate({ to: INBOX_PATH });
      else if (target === 'users' && can(me.permissions, sections.users.operations)) {
        void navigate({ to: '/users' });
      } else if (target === 'audit' && can(me.permissions, sections.audit.operations)) {
        void navigate({ to: '/audit' });
      } else if (target === 'log' && can(me.permissions, sections.log.operations)) {
        void navigate({ to: '/ops/log' });
      } else if (target === 'status' && can(me.permissions, sections.status.operations)) {
        void navigate({ to: '/ops/status' });
      } else if (target === 'jobs') {
        void navigate({ to: '/ops/jobs' });
      }
    },
  });
}
